import React, { useState } from "react";
import axios from "axios";
import WalletActionsConfig from "./admin/WalletActionsConfig";
import { WalletAction, WalletLocation } from "../api/walletActions";

const AdminDashboard: React.FC = () => {
  const [templateId, setTemplateId] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [ttlHours, setTtlHours] = useState(24);
  const [generatedLink, setGeneratedLink] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [walletActions, setWalletActions] = useState<WalletAction[]>([]);
  const [walletLocations, setWalletLocations] = useState<WalletLocation[]>([]);

  const generateLink = async () => {
    setLoading(true);
    setError("");
    setGeneratedLink(null);

    try {
      // This will be replaced with the actual function URL after deployment
      const response = await axios.post("/api/setupLink", {
        templateId,
        maxUses,
        ttlSeconds: ttlHours * 3600,
        platformHint: "auto",
        // Provider-neutral actions/links + locations included at create time.
        // The same arrays can be sent on the update/push path to change them
        // later without reissuing the pass.
        walletActions,
        walletLocations
      });

      setGeneratedLink(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to generate link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto py-12 px-6">
        <h1 className="text-4xl font-bold text-gray-800 mb-8">Admin Dashboard</h1>
        
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-6">Generate Wallet Link</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template ID
              </label>
              <input
                type="text"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., tmpl_123"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Uses
                </label>
                <input
                  type="number"
                  value={maxUses}
                  onChange={(e) => setMaxUses(parseInt(e.target.value) || 1)}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TTL (hours)
                </label>
                <input
                  type="number"
                  value={ttlHours}
                  onChange={(e) => setTtlHours(parseInt(e.target.value) || 24)}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <button
              onClick={generateLink}
              disabled={loading || !templateId}
              className="w-full py-3 bg-purple-600 text-white rounded-md font-semibold hover:bg-purple-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? "Generating..." : "Generate Link"}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {generatedLink && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded">
              <h3 className="font-semibold text-green-800 mb-2">Link Generated Successfully!</h3>
              <div className="space-y-2">
                <div>
                  <span className="font-medium">URL:</span>
                  <a 
                    href={generatedLink.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="ml-2 text-blue-600 hover:underline break-all"
                  >
                    {generatedLink.url}
                  </a>
                </div>
                <div>
                  <span className="font-medium">Link ID:</span>
                  <span className="ml-2 font-mono text-sm">{generatedLink.linkId}</span>
                </div>
                <div>
                  <span className="font-medium">Expires:</span>
                  <span className="ml-2">{new Date(generatedLink.expiresAt).toLocaleString()}</span>
                </div>
                <div>
                  <span className="font-medium">Max Uses:</span>
                  <span className="ml-2">{generatedLink.maxUses}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mb-6">
          <WalletActionsConfig
            initialActions={walletActions}
            initialLocations={walletLocations}
            onChange={({ walletActions: a, walletLocations: l }) => {
              setWalletActions(a);
              setWalletLocations(l);
            }}
          />
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <button className="py-2 px-4 bg-gray-200 rounded hover:bg-gray-300 transition">
              View Templates
            </button>
            <button className="py-2 px-4 bg-gray-200 rounded hover:bg-gray-300 transition">
              View Links
            </button>
            <button className="py-2 px-4 bg-gray-200 rounded hover:bg-gray-300 transition">
              Audit Logs
            </button>
            <button className="py-2 px-4 bg-gray-200 rounded hover:bg-gray-300 transition">
              Analytics
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;